// useArenaPresence — lightweight WebSocket presence for the Sports Arena.
//
// Connects to the backend `/api/arena/ws` endpoint and exchanges positional
// + team state with anyone else currently on the same Campus Quad. This is
// deliberately co-presence, NOT authoritative multiplayer: every visitor
// still runs their own Rapier ball, their own AI, and their own scoreboard.
// The peers list is only used to render ghost avatars on the court so
// visitors can see (and wave at) each other in real time.
//
// Lifecycle:
//   - Connect on mount, tear down on unmount.
//   - Send `update` frames throttled to 5Hz (200ms).
//   - Receive `roster` frames and expose `peers` as a Vector3-like map keyed
//     by peer_id (excluding self).
//
// URL construction uses REACT_APP_BACKEND_URL rewritten to ws(s):// — this
// is the standard React CRA env plumbing already used across the app.

import { useEffect, useMemo, useRef, useState } from "react";

const RECONNECT_MS = 3500;
const SEND_INTERVAL_MS = 200;

function makePeerId() {
  try {
    const existing = window.sessionStorage.getItem("tab-arena-peer-id");
    if (existing) return existing;
  } catch (e) {
    /* ignore */
  }
  // Prefer crypto.randomUUID; fall back to a simple time+random combo.
  let id;
  try {
    id = window.crypto.randomUUID();
  } catch (e) {
    id = `p-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
  try {
    window.sessionStorage.setItem("tab-arena-peer-id", id);
  } catch (e) {
    /* ignore */
  }
  return id;
}

function buildWsUrl({ room, peer, name }) {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  if (!backend) return null;
  const wsProto = backend.startsWith("https://") ? "wss://" : "ws://";
  const host = backend.replace(/^https?:\/\//, "");
  const params = new URLSearchParams({ room, peer, name: name || "Guest" });
  return `${wsProto}${host}/api/arena/ws?${params.toString()}`;
}

/**
 * useArenaPresence
 *
 * @param {object} args
 * @param {string} args.name       Display label for this visitor.
 * @param {() => {pos:number[], team:string, sport:string}} args.getMyState
 *        Called at ~5Hz to build the payload sent up to the server. Callers
 *        should keep this cheap (it runs in a JS setInterval, not useFrame).
 * @param {(kick: {from:string, dir:number[], power:number, sport:string}) => void} [args.onKick]
 *        Called every time a remote peer's kick lands (server-side relay).
 *        Recipients decide locally whether to apply the impulse based on
 *        their own Match Invite toggle.
 * @returns {{
 *   peerId: string,
 *   connected: boolean,
 *   peers: Record<string, {pos:number[], team:string, sport:string, name:string, ts:number}>,
 *   peerCount: number,
 *   sendKick: (kick: {dir:number[], power:number, sport:string}) => void,
 * }}
 */
export function useArenaPresence({ name = "Guest", getMyState, onKick }) {
  const peerId = useMemo(() => makePeerId(), []);
  const [connected, setConnected] = useState(false);
  const [peers, setPeers] = useState({}); // peer_id -> state (excludes self)
  const wsRef = useRef(null);
  const sendTimerRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const closedByUserRef = useRef(false);
  const stateFnRef = useRef(getMyState);
  const kickFnRef = useRef(onKick);
  stateFnRef.current = getMyState;
  kickFnRef.current = onKick;

  useEffect(() => {
    const url = buildWsUrl({ room: "quad", peer: peerId, name });
    if (!url) return undefined;

    const open = () => {
      let ws;
      try {
        ws = new WebSocket(url);
      } catch (err) {
        // Some browsers throw synchronously on ws:// against https origins.
        console.warn("[arena-presence] WebSocket construction failed", err);
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        // Start throttled state broadcast.
        sendTimerRef.current = setInterval(() => {
          const s = stateFnRef.current && stateFnRef.current();
          if (!s || !ws || ws.readyState !== WebSocket.OPEN) return;
          try {
            ws.send(JSON.stringify({ type: "update", state: s }));
          } catch (e) {
            /* ignore send errors — reconnect handler will pick it up */
          }
        }, SEND_INTERVAL_MS);
      };

      ws.onmessage = (evt) => {
        let payload;
        try {
          payload = JSON.parse(evt.data);
        } catch (e) {
          return;
        }
        if (!payload || typeof payload !== "object") return;
        if (payload.type === "roster" && payload.peers) {
          // Exclude self so callers can iterate freely without filtering.
          const next = {};
          for (const [pid, st] of Object.entries(payload.peers)) {
            if (pid === peerId) continue;
            next[pid] = st;
          }
          setPeers(next);
        } else if (payload.type === "kick") {
          // Relay to the arena scene. Gating (whether to apply the impulse)
          // happens at the callsite based on the local Match Invite toggle.
          if (kickFnRef.current) kickFnRef.current(payload);
        }
      };

      ws.onerror = () => {
        // Errors surface as an eventual close; nothing to do here.
      };

      ws.onclose = () => {
        setConnected(false);
        if (sendTimerRef.current) {
          clearInterval(sendTimerRef.current);
          sendTimerRef.current = null;
        }
        wsRef.current = null;
        if (closedByUserRef.current) return;
        reconnectTimerRef.current = setTimeout(open, RECONNECT_MS);
      };
    };

    open();

    return () => {
      closedByUserRef.current = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (sendTimerRef.current) {
        clearInterval(sendTimerRef.current);
        sendTimerRef.current = null;
      }
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch (e) {
          /* ignore */
        }
        wsRef.current = null;
      }
    };
  }, [peerId, name]);

  const sendKick = useMemo(
    () => (kick) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      try {
        ws.send(JSON.stringify({ type: "kick", ...kick }));
      } catch (e) {
        /* ignore send errors */
      }
    },
    []
  );

  const peerCount = Object.keys(peers).length;
  // Number of remote peers who currently have Match Invite ON — used by the
  // HUD to show a "N READY" matchmaking badge next to the local toggle.
  let readyCount = 0;
  for (const st of Object.values(peers)) {
    if (st && st.invite) readyCount++;
  }
  return { peerId, connected, peers, peerCount, readyCount, sendKick };
}
